import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classNames from 'classnames';
import PrimarySwitch from 'components/shared/PrimarySwitch';
import MDButton from 'components/themed/MDButton';
import MDInput from 'components/themed/MDInput';
import { Form, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import {
    useGetBusinessSuscriptionPlanQuery,
    useUpdateBusinessSubscriptionPlanMutation,
} from 'redux/services/spotdif/subscriptionPlan';
import * as Yup from 'yup';
import './style.scss';
import { IBusinessSubscriptionPlan } from 'contracts/view-models/IBusinessSubscriptionPlan';
import MDTypography from 'components/themed/MDTypography';

interface ISubscriptionPrice {
    businessId: string;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    additionalAppointmentPrice: Yup.number()
        .min(0, 'Price must be positive')
        .required('Additional appointment price is required'),
    planAppointments: Yup.number().min(1, 'Must have at least 1 appointment').required('Plan appointments is required'),
});

const PlanForm: React.FC<{ plan: IBusinessSubscriptionPlan; onSubmit: (values: any) => void }> = ({
    plan,
    onSubmit,
}) => {
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: plan,
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            onSubmit(values);
            setSubmitting(false);
        },
    });

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
                <div className="card-content">
                    <MDInput
                        label="Plan Name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />

                    <MDInput
                        label="Description"
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        rows={3}
                        error={formik.touched.description && Boolean(formik.errors.description)}
                        helperText={formik.touched.description && formik.errors.description}
                    />

                    <MDInput
                        label="Additional Appointment Price"
                        name="additionalAppointmentPrice"
                        value={formik.values.additionalAppointmentPrice}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        type="number"
                        error={
                            formik.touched.additionalAppointmentPrice &&
                            Boolean(formik.errors.additionalAppointmentPrice)
                        }
                        helperText={
                            formik.touched.additionalAppointmentPrice && formik.errors.additionalAppointmentPrice
                        }
                    />

                    <MDInput
                        label="Plan Appointments"
                        name="planAppointments"
                        value={formik.values.planAppointments}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        type="number"
                        error={formik.touched.planAppointments && Boolean(formik.errors.planAppointments)}
                        helperText={formik.touched.planAppointments && formik.errors.planAppointments}
                    />

                    <div className="switch-container">
                        <PrimarySwitch
                            name="isActive"
                            checked={formik.values.isActive}
                            onChange={formik.handleChange}
                        />
                        <span>Active</span>
                    </div>

                    <MDButton
                        className="button button-primary update-button"
                        type="button"
                        onClick={() => formik.handleSubmit()}
                        disabled={!formik.isValid || formik.isSubmitting}
                    >
                        Update Plan
                    </MDButton>
                </div>
            </Form>
        </FormikProvider>
    );
};

const SubscriptionPrice: React.FC<ISubscriptionPrice> = ({ businessId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { data } = useGetBusinessSuscriptionPlanQuery({ id: businessId });
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
    const [updateBusinessSubscriptionPlan] = useUpdateBusinessSubscriptionPlanMutation();

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const handleSubmit = async (values: any) => {
        try {
            // TODO: Replace with actual update mutation when implemented
            await updateBusinessSubscriptionPlan({ id: values._id, body: values })
                .unwrap()
                .then(() => {
                    enqueueSnackbar('Plan updated successfully', { variant: 'success' });
                })
                .catch(() => {
                    enqueueSnackbar('Failed to update plan', { variant: 'error' });
                });

            console.log('Updating plan:', values);
            enqueueSnackbar('Plan updated successfully', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to update plan', { variant: 'error' });
        }
    };

    return (
        <div className="form-groups subscription-price">
            <div className="subscription-cards">
                {data && data.data.length > 0 ? (
                    <>
                        {data.data.map((plan, index) => (
                            <Accordion
                                key={plan._id}
                                expanded={expandedPanel === `plan-${index}`}
                                onChange={handleAccordionChange(`plan-${index}`)}
                                className={classNames('subscription-card', { active: plan.isActive })}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`plan-${index}-content`}
                                    id={`plan-${index}-header`}
                                >
                                    <MDTypography variant="h6">
                                        {plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}{' '}
                                        {plan.isActive ? '(Active)' : '(Inactive)'}
                                    </MDTypography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <PlanForm plan={plan} onSubmit={handleSubmit} />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </>
                ) : (
                    <p className="no-subscription">No subscription plans available for this business.</p>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPrice;
